<template>
	<div v-if="!isLoading">
		<div v-if="isUserAllowedInLevel1 || isAuthenticatedUserInGroupAdmin || !$route.params.id">
			<BaseHeader :title="isNewUser ? $t('add_user').toString() : $t('user_data').toString()" />
			<div class="flex px-8 mt-8 grid-cols-2 space-x-6" v-if="isAuthenticatedUserInGroupAdmin && isOtherUser">
				<BaseCheckbox
					label="Administrator?"
					v-model="isAdmin"
					:disabled="!isAdminCheckboxAllowed"
					:ask-before-change="true"
					:ask-before-change-question="isAdmin ? $t('user_to_employee').toString() : $t('user_to_admin').toString()"
				/>
				<BaseCheckbox
					v-if="isUserAllowedInLevel5or6"
					:label="$t('marketing_permissions')"
					v-model="hasAdPermission"
					:disabled="evaluateDisabled"
					:ask-before-change="true"
					:ask-before-change-question="hasAdPermission ? $t('remove_ad_permissions').toString() : $t('add_ad_permissions').toString()"
				/>
			</div>
			<div class="px-8 flex" :class="!isNewUser && !isOtherUser ? 'mt-8' : ''">
				<div class="md:gap-1 flex-grow items-baseline w-1" :class="!isNewUser ? 'md:mr-4' : 'md:mr-8'" v-if="!isNewUser">
					<BaseInput :label="$t('system_access_since')" ref="BaseInput.createdAt" :disabled="true" type="text" v-model="createdDate" />
				</div>
				<div class="md:gap-1 flex-grow items-baseline w-1" :class="!isNewUser && !isOtherUser ? 'md:mr-4' : ''">
					<BaseInput
						:label="$t('e_mail')"
						ref="BaseInput.email"
						:required="true"
						type="text"
						validationtype="email"
						:validation="regex.email"
						v-model="getUser.email"
						@focusout.native="readEmailAndValidate"
						:error-message="$t('no_valid_e_mail')"
					/>
					<BaseInput
						:label="$t('password')"
						ref="BaseInput.password"
						type="text"
						v-model="password"
						@input="checkPasswordInput($event)"
						:required="isNewUser"
						v-if="!isNewUser && !isOtherUser"
					/>
					<PasswordStrengthMeter ref="PasswordStrengthMeter" v-model="password" v-if="!isNewUser && !isOtherUser"></PasswordStrengthMeter>
					<div class="flex justify-between mt-12" v-if="!isNewUser && !isOtherUser">
						<BaseButton @click="generateSafePassword">{{ $t('generate_secure_password') }}</BaseButton>
						<a href="#" @click="openPasswordInfoBox" class="md:block lg:hidden bg-sp btn block btn-info">
							<font-awesome-icon :icon="['fas', 'info']" class="w-4 h-4 text-white"></font-awesome-icon>
						</a>
					</div>
				</div>

				<div class="w-1 md:gap-1 flex-grow items-baseline md:hidden lg:block" v-if="!isNewUser && !isOtherUser">
					<BaseCard title="Passwortinfo">
						<p v-if="!isNewUser">{{ $t('leave_empty_for_no_change') }}<br /><br /></p>
						<span class="font-bold">{{ $t('least_requirements') }}</span
						><br />
						<ul class="list-disc list-inside ml-4">
							<li>{{ $t('min_eight_chars') }}</li>
							<li>{{ $t('min_one_cap') }}</li>
							<li>{{ $t('min_one_low_char') }}</li>
							<li>{{ $t('min_one_number') }}</li>
							<li>{{ $t('min_one_special') }}</li>
							<li>{{ $t('no_spaces') }} <strong>keine</strong> {{ $t('no_spaces_2') }}</li>
						</ul>
					</BaseCard>
				</div>
			</div>

			<div class="px-8">
				<PersonNameForm v-model="getUser.person" :show-company="false" ref="PersonNameForm" />
				<div class="flex w-full">
					<BaseInput
						:label="isUserAllowedInLevel2 ? $t('mobile_number') : $t('reachable_by_phone')"
						ref="BaseInput.mobileNumber"
						:required="true"
						type="text"
						v-model="getUser.person.phoneNumber"
						class="w-1/2 mr-4"
						validationtype="phone"
					/>
					<BaseTextarea class="w-1/2" :label="$t('e_mail_signature')" v-model="getUser.mail_signature" />
				</div>
			</div>
			<div v-if="isUserAllowedInLevel2 && !isNewUser" class="flex ml-10">
				<div>
					<BaseButton @click="openUpload" class="w-full sm:w-auto mt-4 sm:mt-0">
						<input type="file" ref="file" accept="image/*" @change="chosedUpload" class="hidden" />
						<font-awesome-icon :icon="['fas', 'image']" class="w-5 h-5 pr-1" size="lg" />
						{{ $t('upload_sign_stamp') }}
					</BaseButton>
					<BaseSign
						:background-image="backgroundImage"
						ref="BaseInput.company.signpad"
						v-model="getUser.userSign"
						@clear="
							backgroundImage = null;
							$store.commit('setConfirmLeaving', $t('leave_page'));
						"
					/>
				</div>
			</div>

			<Footer class="mx-8">
				<template #backButton>
					<div>
						<BaseButton @click="$router.go(-1)">{{ $t('general.back') }}</BaseButton>
					</div>
				</template>

				<template #continueButton>
					<div class="w-full flex justify-end">
						<div v-if="isNewUser">
							<BaseCheckbox v-model="sentCredentials" :label="$t('send_credentials_per_mail')" class="mr-4 mt-2" />
						</div>
						<div>
							<SaveButton :disabled="disableButton" @click="save">{{
								isRepairShopOrCarDealerShip ? $t('general.next') : isNewUser ? $t('general.save') : $t('save_changes')
							}}</SaveButton>
						</div>
					</div>
				</template>
			</Footer>
		</div>
		<div v-else>
			<Forbidden />
		</div>
	</div>
	<LoadingSpinner v-else />
</template>

<script>
import BaseInput from '@/components/forms/inputs/BaseInput';
import BaseButton from '@/components/forms/buttons/BaseButton';
import BaseHeader from '@/components/navigation/BaseHeader';
import Validate from '@/mixins/Validate';
import BaseCard from '@/components/damagecases/forms/BaseCard';
import PasswordStrengthMeter from '@/components/damagecases/forms/PasswordStrengthMeter';
import { getStringFromDate } from '@/plugins/Utils';
import { AuthenticationApiFactory, UserApiFactory, UserEmailApiFactory, UserGroupApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import RegexpHelper from '@/mixins/RegexpHelper';
import CityInput from '@/components/forms/CityInput';
import PersonNameForm from '@/components/forms/PersonNameForm';
import BaseCheckbox from '@/components/forms/inputs/BaseCheckbox';
import Authentication from '@/mixins/Authentication';
import { UserGroups } from '@/helpers/enums';
import Forbidden from '@/views/default/Forbidden';
import BaseSign from '@/components/forms/inputs/BaseSign';
import BaseTextarea from '@/components/forms/inputs/BaseTextarea';
import UserGroupRepository from '@/api/repositories/UserGroupRepository';
import Footer from '@/components/damagecases/Footer';
import SaveButton from '@/components/forms/buttons/SaveButton';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';
import CleanBaseRadio from '@/components/forms/inputs/CleanBaseRadio.vue';

export default {
	name: 'Userprofile',
	components: {
		LoadingSpinner,
		SaveButton,
		Footer,
		BaseTextarea,
		Forbidden,
		BaseCheckbox,
		PersonNameForm,
		PasswordStrengthMeter,
		BaseCard,
		BaseInput,
		BaseHeader,
		BaseButton,
		BaseSign,
	},
	mixins: [Validate, RegexpHelper, Authentication, LoadingSpinner],
	data() {
		return {
			password: null,
			isSaveAllowed: true,
			otherUser: { person: { city: {} } },
			isOtherUser: false,
			sentCredentials: true,
			isAdmin: false,
			hasAdPermission: false,
			usergroups: null,
			timer: false,
			mail_valid: true,
			backgroundImage: null,
			isLoading: true,
			disableButton: false,
		};
	},
	props: {
		isCreateFromNewCompany: { default: false },
		isRepairShopOrCarDealerShip: { default: false },
		defaultUser: { default: false },
	},
	computed: {
		evaluateDisabled() {
			if (this.isAdminCheckboxAllowed) {
				if (this.isAdmin) {
					// eslint-disable-next-line vue/no-side-effects-in-computed-properties
					this.hasAdPermission = true;
				}
				return this.isAdmin;
			} else {
				return true;
			}
		},
		getUser() {
			if (!this.isOtherUser) {
				return this.$store.getters.getUser;
			} else {
				return this.otherUser;
			}
		},
		createdDate() {
			return getStringFromDate(new Date((this.getUser.created_at || this.getUser.createdAt) * 1000));
		},
		isNewUser() {
			return this.$route.meta.action === 'new';
		},
		isAdminCheckboxAllowed() {
			return this.isAuthenticatedUserInGroupAdmin && !this.isCreateFromNewCompany && this.isOtherUser;
		},
	},
	mounted() {
		if (this.isUserAllowedInLevel1 || this.isAuthenticatedUserInGroupAdmin || !this.$route.params.id) {
			this.$store.dispatch('updateAuthenticatedUser');
		} else {
			this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
		}
	},
	beforeMount() {
		this.init();
	},
	watch: {
		$route() {
			this.isOtherUser = this.isNewUser || (this.$route.params.id && this.getUser.id !== parseInt(this.$route.params.id));
			this.password = null;
			this.init();
		},
	},
	methods: {
		setSelectedPostal(event) {
			this.selectedPostalOption = event;
		},
		setDefaultAsOtherUser() {
			this.otherUser = this.defaultUser;
		},
		init() {
			this.isOtherUser = this.isNewUser || (this.$route.params.id && this.getUser.id !== parseInt(this.$route.params.id));
			if (this.isOtherUser) {
				this.isLoading = true;
			} else {
				this.isLoading = false;
			}
			if (this.$route.params.id) {
				UserApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
					.userIdGet(this.$route.params.id)
					.then(response => {
						this.otherUser = response.data.data;
						this.hasAdPermission = response.data.data.is_ad_admin;
						this.loadUsergroups();
						this.isLoading = false;
					});
			} else {
				this.loadUsergroups();
				this.isLoading = false;
			}
		},
		loadUsergroups() {
			if (this.isAuthenticatedUserInGroupAdmin) {
				UserGroupRepository.getByLevel(this.getUser.level).then(response => {
					this.usergroups = [...response.data.data];
					if (this.isCreateFromNewCompany) {
						this.isAdmin = true;
					} else {
						this.isAdmin = this.usergroups.find(group => group.name === UserGroups.ADMIN).id === this.getUser?.usergroup?.id;
						this.hasAdPermission = this.getUser.isAdAdmin;
					}
				});
			}
		},
		generateSafePassword: function() {
			const length = Math.floor(Math.random() * (24 - 13) + 12);
			const keyListAlpha = 'abcdefghijklmnopqrstuvwxyz';
			const keyListALPHA = 'abcdefghijklmnopqrstuvwxyz'.toUpperCase();
			const keyListInt = '123456789123456789';
			const keyListSpec = '!@#$%^&*()_+~`|}{[]:;?><,./-=';
			let keys = keyListAlpha + keyListALPHA + keyListInt + keyListSpec;

			keys = keys.split('').sort(function() {
				return 0.5 - Math.random();
			});

			let password = '';

			for (let i = 0; i < length; i++) {
				password += keys[Math.floor(Math.random() * keys.length)];
			}

			password = password
				.split('')
				.sort(function() {
					return 0.5 - Math.random();
				})
				.join('');

			this.password = password;

			if (this.checkPasswordStrength(this.password) < 5) {
				this.generateSafePassword();
			}

			this.isSaveAllowed = true;
		},
		checkPasswordInput: function($event) {
			this.password = $event.replaceAll(' ', '');
			const pwCheck = this.checkPasswordStrength($event.replaceAll(' ', ''));
			this.isSaveAllowed = !($event !== '' && pwCheck < 5);
			// TODO entfernen, wenn Produktiv!
			// this.isSaveAllowed = true;
		},
		checkPasswordStrength: function($event) {
			return this.$refs['PasswordStrengthMeter'].checkPasswordStrength($event);
		},
		save() {
			this.checkEmailIsAlreadyAssigned();
			if (this.validate()) {
				let promise;
				let successMessage;
				this.disableButton = true;
				if (this.isNewUser) {
					promise = this.createUser();
					successMessage = this.sentCredentials ? this.$t('employee_saved_successfully_credentials_sent') : this.$t('employee_saved_successfully');
					this.disableButton = false;
				} else {
					promise = this.updateUser();
					successMessage = this.$t('changes_stored');
					this.disableButton = false;
				}
				// Wenn User mit der Firma angelegt wird, erhält man keinen Promise. Wird ein weiterer User angelegt erhält man bei createUser einen Promise.
				if (promise) {
					promise
						.then(response => {
							this.isLoading = false;
							this.$swal({
								title: this.$t('general.success'),
								text: successMessage,
								icon: 'success',
							}).then(response => {
								this.$store.commit('setConfirmLeaving', false);
								if (this.isOtherUser) {
									this.$router.push({ name: 'employees' });
								}
							});
						})
						.catch(error => {
							this.isLoading = false;
							if (error.response.status === 430) {
								this.$swal(this.$t('general.attention').toString(), this.$t('e_mail_address_exists').toString(), 'warning');
							} else {
								this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
							}
						});
				}
			} else {
				this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
			}
		},
		updateUser() {
			return AuthenticationApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.userIdPut(this.getUser.id, {
					email: this.getUser.email,
					password: this.password,
					is_ad_admin: this.hasAdPermission ? 1 : 0,
					person: this.getUser.person,
					mail_signature: this.getUser.mail_signature,
					usergroup: this.isAdmin
						? this.usergroups?.find(group => group.name === UserGroups.ADMIN)
						: this.usergroups?.find(group => group.name === UserGroups.EMPLOYEE),
					userSign: this.getUser.userSign,
				})
				.then(_ => {
					if (!this.isOtherUser) {
						this.$store.dispatch('updateAuthenticatedUser');
					}
				});
		},
		createUser() {
			const user = {
				email: this.getUser.email,
				is_ad_admin: this.hasAdPermission,
				person: this.getUser.person,
				notifyNewUser: this.sentCredentials,
				userSign: this.getUser.userSign,
				mail_signature: this.getUser.mail_signature,
			};

			if (!this.isCreateFromNewCompany) {
				user.usergroup =
					(this.isAdmin
						? this.usergroups?.find(group => group.name === UserGroups.ADMIN)
						: this.usergroups?.find(group => group.name === UserGroups.EMPLOYEE)) || this.usergroups[0];
				return UserApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios).userPost(user);
			} else {
				this.$store.commit('setConfirmLeaving', false);
				this.$emit('save', user);
			}
		},
		openPasswordInfoBox() {
			this.$swal(
				'Passwortinfo',
				'<div class="text-left"><p>Wenn Sie das Passwort nicht ändern möchten, lassen Sie das Feld für das Passwort leer!<br /><br /></p>\n' +
					'<span class="font-bold">Mindestanforderungen:</span><br />\n' +
					'<ul class="list-disc list-inside ml-4">\n' +
					'<li>min. 8 Zeichen</li>\n' +
					'<li>min. 1 Großbuchstabe enthalten</li>\n' +
					'<li>min. 1 Kleinbuchstabe enthalten</li>\n' +
					'<li>min. 1 Zahl enthalten</li>\n' +
					'<li>min. 1 Sonderzeichen enthalten</li>\n' +
					'<li>es dürfen <span class="font-bold">keine</span> Leerzeichen enthalten sein</li>\n' +
					'</ul></div>',
				'info',
			);
		},
		readEmailAndValidate() {
			if (this.$refs['BaseInput.email'].validate()) {
				clearTimeout(this.timer);
				this.timeouteId = setTimeout(() => this.checkEmailIsAlreadyAssigned(), 1000);
			}
		},
		checkEmailIsAlreadyAssigned() {
			return UserEmailApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.usersCheckmailGet(this.getUser.email, this.getUser.id)
				.then(response => {
					this.$refs['BaseInput.email'].isValid = response.data.is_free;
				});
		},
		openUpload() {
			this.$refs.file.click();
		},
		chosedUpload() {
			const file = this.$refs.file.files[0];
			const reader = new FileReader();

			if (!file.type.startsWith('image/')) {
				return false;
			}

			reader.onload = e => {
				this.backgroundImage = e.target.result;
			};

			reader.readAsDataURL(file);
		},
	},
};
</script>

<style scoped></style>
